/**
 * "API health" gadget (Verify): how many endpoints the services declare (the API tab's `GET /api`), and from the Traces
 * map of the window Flow keeps (shared with Requests and Errors) the services by p95, slowest first, and the operations
 * that failed most. The map times whole services, not single endpoints: the gadget says so instead of guessing.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { apiHealth } from '../../../utils/homeGadgets/verify';
import { formatMs } from '../../traces/waterfallLayout';
import { windowLabel } from '../RequestsGadget';
import GadgetCard, { Quiet, TabLink } from './GadgetCard';
import { useEndpoints, useTracesMap } from './sources';

export default function ApiHealthGadget({ projectId, onOpenApi = null, onOpenTraces = null }) {
  const api = useEndpoints(projectId);
  const map = useTracesMap(projectId);
  const health = apiHealth({ api: api.data, map: map.data });
  const tracingOff = map.data?.enabled === false;
  let endpointsLine;
  if (health.endpoints !== null) {
    endpointsLine = <><strong className="gd-num">{health.endpoints}</strong> {health.endpoints === 1 ? 'endpoint' : 'endpoints'} in {health.services} {health.services === 1 ? 'service' : 'services'}</>;
  } else if (api.loading) {
    endpointsLine = 'Reading the endpoints…';
  } else {
    endpointsLine = 'The endpoints could not be read just now.';
  }
  let timing;
  if (!map.data && map.loading) { timing = <Quiet>Reading the requests…</Quiet>; }
  else if (!map.data && map.error) { timing = <Quiet>The requests could not be read just now.</Quiet>; }
  else if (tracingOff) { timing = <Quiet>Tracing is off, so how fast the services answer is not known.</Quiet>; }
  else if (health.slowest.length === 0) { timing = <Quiet testId="gadget-empty">No request in the {windowLabel(map.data)} to time.</Quiet>; }
  else {
    timing = (
      <>
        <p className="gd-note">Slowest services, p95 in the {windowLabel(map.data)}:</p>
        <ul className="gd-rows">
          {health.slowest.map((s) => (
            <li className="gd-row" data-testid={`api-slow-${s.service}`} key={s.service}>
              <strong>{s.service}</strong>
              <span className="gd-grow ph-muted">{s.calls} {s.calls === 1 ? 'request' : 'requests'}{s.errors > 0 ? <span className="gd-bad"> · {s.errors} failed</span> : null}</span>
              <span className="gd-num">{formatMs(s.p95Ms)}</span>
            </li>
          ))}
        </ul>
        {health.failing.length > 0 && (
          <p className="gd-note" data-testid="api-failing">Failing most: {health.failing.map((op) => `${op.name} (${op.errors})`).join(', ')}</p>
        )}
      </>
    );
  }
  return (
    <GadgetCard links={<><TabLink onClick={onOpenApi}>API</TabLink><TabLink onClick={onOpenTraces}>Traces</TabLink></>} testId="api-health" title="API health">
      <p className="gd-note" data-testid="api-endpoints">{endpointsLine}</p>
      {timing}
    </GadgetCard>
  );
}

ApiHealthGadget.propTypes = { onOpenApi: PropTypes.func, onOpenTraces: PropTypes.func, projectId: PropTypes.string.isRequired };
